// 符號定義 — 已驗證自 Unity Data/SymbolData.asset。
// color 為佔位顏色（尚未接入真實美術資源，見 docs/UNITY_SOURCE_ANALYSIS.md §7 的 Art 資產清單）。

export type SymbolTier = 'wild' | 'high' | 'low' | 'scatter'

export interface SymbolDef {
  readonly id: number
  readonly name: string
  readonly tier: SymbolTier
  /** 佔位顯示色（真實美術資源接入後移除）。 */
  readonly color: number
}

export const SYMBOLS: readonly SymbolDef[] = [
  { id: 0, name: 'WW', tier: 'wild', color: 0xf5c542 },
  { id: 1, name: 'H1', tier: 'high', color: 0xd9483d },
  { id: 2, name: 'H2', tier: 'high', color: 0xd97a3d },
  { id: 3, name: 'H3', tier: 'high', color: 0x3d8dd9 },
  { id: 4, name: 'H4', tier: 'high', color: 0x5ec26a },
  { id: 5, name: 'H5', tier: 'high', color: 0x9c5ec2 },
  { id: 6, name: 'H6', tier: 'high', color: 0xc25e9c },
  { id: 7, name: 'H7', tier: 'high', color: 0x5ec2b0 },
  { id: 8, name: 'LA', tier: 'low', color: 0x8a94a6 },
  { id: 9, name: 'LK', tier: 'low', color: 0x8a94a6 },
  { id: 10, name: 'LQ', tier: 'low', color: 0x8a94a6 },
  { id: 11, name: 'LJ', tier: 'low', color: 0x8a94a6 },
  { id: 12, name: 'SS', tier: 'scatter', color: 0xff5fb0 },
]

const SYMBOLS_BY_ID = new Map(SYMBOLS.map((symbol) => [symbol.id, symbol]))

export function symbolById(id: number): SymbolDef {
  const symbol = SYMBOLS_BY_ID.get(id)
  if (symbol === undefined) {
    throw new Error(`未知符號 ID: ${id}`)
  }
  return symbol
}

export const PAYABLE_SYMBOL_IDS: readonly number[] = SYMBOLS.filter((s) => s.tier !== 'scatter').map(
  (s) => s.id,
)

/**
 * 可長條化（跨格大符號）的符號 id。已驗證自 Unity Data/SymbolData.asset：
 * 只有 WW/H1/H2/H3/H4/SS 這 6 個有 2x/3x/4x 尺寸貼圖與 `_1x{n}` Spine 動畫；
 * H5/H6/H7 與所有低分牌（LA/LK/LQ/LJ）只有單一尺寸美術，恆為 1 格。
 */
export const TALL_CAPABLE_SYMBOL_IDS: ReadonlySet<number> = new Set([0, 1, 2, 3, 4, 12])

/** 該符號是否可長條化（有多尺寸貼圖）。 */
export function isTallCapable(symbolId: number): boolean {
  return TALL_CAPABLE_SYMBOL_IDS.has(symbolId)
}

/**
 * 恆 1 格符號的木框階級貼圖 key（已驗證自 SymbolData.asset 的 BackgroundSprite 指派）：
 * LA/LK/LQ/LJ→woodBg1、H7→woodBg2、H6→woodBg3、H5→woodBg4。
 * 可長條化的 6 個符號 BackgroundSprite=0（木框已烘進符號貼圖本身），回傳 null（不另加背景框）。
 */
export function woodBackgroundKeyFor(symbolId: number): 'woodCell1' | 'woodCell2' | 'woodCell3' | 'woodCell4' | null {
  if (isTallCapable(symbolId)) return null
  switch (symbolId) {
    case 7: // H7
      return 'woodCell2'
    case 6: // H6
      return 'woodCell3'
    case 5: // H5
      return 'woodCell4'
    default: // LA/LK/LQ/LJ
      return 'woodCell1'
  }
}
